import { type DragEvent, useRef, useState } from 'react';

/** Accept PDFs and images (matches the upload API). */
function accepted(file: File): boolean {
  return file.type === 'application/pdf' || file.type.startsWith('image/') || /\.pdf$/i.test(file.name);
}

/**
 * Drag-and-drop file upload for a region. Returns an `isOver` flag (for
 * highlighting) and handlers to spread onto the drop target. A drag-enter
 * counter keeps `isOver` stable while the cursor moves over child elements.
 */
export function useFileDrop(onFiles: (files: File[]) => void) {
  const [isOver, setIsOver] = useState(false);
  const depth = useRef(0);

  const onDragEnter = (e: DragEvent) => {
    if (!Array.from(e.dataTransfer.types).includes('Files')) return;
    e.preventDefault();
    depth.current += 1;
    setIsOver(true);
  };
  const onDragOver = (e: DragEvent) => {
    if (Array.from(e.dataTransfer.types).includes('Files')) e.preventDefault();
  };
  const onDragLeave = (e: DragEvent) => {
    e.preventDefault();
    depth.current -= 1;
    if (depth.current <= 0) {
      depth.current = 0;
      setIsOver(false);
    }
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    depth.current = 0;
    setIsOver(false);
    const files = Array.from(e.dataTransfer.files).filter(accepted);
    if (files.length) onFiles(files);
  };

  return { isOver, dropHandlers: { onDragEnter, onDragOver, onDragLeave, onDrop } };
}
